import { useDroppable } from '@dnd-kit/core';
import type { CalendarStripItem, Destination } from '@konvert/shared';
import { formatMinutesRu, isoWeekday, parseLocalDate } from '@konvert/domain';
import { t } from '../lib/i18n.js';

const WEEKDAY_SHORT = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

function DropTarget({
  id,
  destination,
  children,
  className = '',
}: {
  id: string;
  destination: Destination;
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id, data: { destination } });
  return (
    <div
      ref={setNodeRef}
      className={`${className} transition-colors duration-150 ${
        isOver ? 'ring-2 ring-[var(--kraft-dark)]' : ''
      }`}
    >
      {children}
    </div>
  );
}

/** 14-day strip with a load indicator per day (SPEC 11.2). */
export function CalendarStrip({
  items,
  today,
  onPick,
}: {
  items: CalendarStripItem[];
  today: string;
  onPick: (date: string) => void;
}): JSX.Element {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {items.map((item) => {
        const { day } = parseLocalDate(item.date);
        const ratio = item.max_minutes ? Math.min(1, item.time_load_minutes / item.max_minutes) : 0;
        return (
          <DropTarget
            key={item.date}
            id={`day:${item.date}`}
            destination={{ kind: 'day', date: item.date }}
            className="shrink-0 rounded-[var(--radius-card)]"
          >
            <button
              type="button"
              data-testid="strip-day"
              data-date={item.date}
              onClick={() => onPick(item.date)}
              className={`paper-card w-16 px-2 py-2 text-center ${
                item.date === today ? 'border-[var(--kraft-dark)]' : ''
              }`}
            >
              <span className="block text-xs text-ink-soft">
                {WEEKDAY_SHORT[isoWeekday(item.date) - 1]}
              </span>
              <span className="block text-lg leading-tight text-ink">{day}</span>
              <span className="mt-1 block h-1 w-full rounded-full bg-[var(--paper-deep)]">
                <span
                  className={`block h-1 rounded-full ${item.exceeded ? 'bg-brick' : 'bg-[var(--kraft-dark)]'}`}
                  style={{ width: `${ratio * 100}%` }}
                />
              </span>
              <span className="mt-1 block text-[10px] text-ink-soft">
                {item.cards_count > 0 ? item.cards_count : '·'}
                {item.has_conflicts ? ' ◷' : ''}
              </span>
            </button>
          </DropTarget>
        );
      })}
    </div>
  );
}

const DRAWERS: Array<{ label: string; destination: Destination }> = [
  { label: 'Неделя', destination: { kind: 'week', date: '' } },
  { label: 'Месяц', destination: { kind: 'month', date: '' } },
  { label: 'Год', destination: { kind: 'year', date: '' } },
  { label: 'Бэклог', destination: { kind: 'inbox' } },
  { label: 'Someday', destination: { kind: 'someday' } },
  { label: 'Проекты', destination: { kind: 'projects' } },
  { label: 'Копилка', destination: { kind: 'done' } },
  { label: 'Корзина', destination: { kind: 'trash' } },
  { label: 'Архив заметок', destination: { kind: 'notes_archive' } },
];

export function Drawers({
  today,
  onOpen,
}: {
  today: string;
  onOpen: (destination: Destination) => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-2">
      {DRAWERS.map((drawer) => {
        const destination =
          'date' in drawer.destination ? { ...drawer.destination, date: today } : drawer.destination;
        return (
          <DropTarget
            key={drawer.label}
            id={`drawer:${drawer.label}`}
            destination={destination as Destination}
            className="rounded-[var(--radius-card)]"
          >
            <button
              type="button"
              onClick={() => onOpen(destination as Destination)}
              className="w-full rounded-[var(--radius-card)] border border-[var(--line)] bg-[var(--kraft)]/40 px-3 py-3 text-left text-sm text-ink"
            >
              {drawer.label}
            </button>
          </DropTarget>
        );
      })}
    </div>
  );
}

export function Banner({
  text,
  count,
  onClick,
}: {
  text: string;
  count: number;
  onClick?: () => void;
}): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-[var(--radius-card)] border border-[var(--line)] bg-[var(--kraft)]/50 px-3 py-2 text-left text-sm text-ink"
    >
      {text}: {count}
    </button>
  );
}

export function formatLoadShort(minutes: number): string {
  return formatMinutesRu(minutes);
}

export { t };
